import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { authFetch, apiUrl } from "../../../utils/api";

/**
 * UsageTab - Usage tracking only
 *
 * Shows model usage this period (analytics only — flat $297 pricing, nothing bills from this).
 * Billing management (cancel, payment, overage) lives in BillingTab.
 */
const UsageTab = () => {
  const { user } = useUser();
  const [usageData, setUsageData] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  // Flat $297 pricing + BYOK: EVERYONE is unlimited. Users bring their own
  // Claude gas via OAuth, so there are no caps, no metering, and no overage
  // to surface. The usage view is light analytics only — never a limit.
  const isUnlimited = true;

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [usageRes, billingRes, creditsRes] = await Promise.all([
        authFetch(apiUrl(`/api/llm/usage?period=${period}`)),
        authFetch(apiUrl("/api/billing/status")),
        authFetch(apiUrl("/api/usage/credits")),
      ]);

      const usageJson = await usageRes.json();
      const billingJson = await billingRes.json();
      const creditsJson = await creditsRes.json();

      setUsageData(usageJson);
      setBillingStatus({ ...billingJson, ...creditsJson });
    } catch (error) {
      console.error("Failed to load data:", error);
      setUsageData(null);
      setBillingStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>📊 Usage</h3>
      <p style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: -6, marginBottom: 14 }}>
        For your peace of mind — Optimus never meters or marks up your AI usage. This is what ran
        through your own Claude account, at cost.
      </p>
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}>
          Loading...
        </div>
      </div>
    );
  }

  const hasNoUsage = !usageData?.total?.calls || usageData.total.calls === 0;
  const includedAmount = billingStatus?.includedAmount ?? 297;
  const usedAmount = billingStatus?.usedAmount ?? 0;
  const remainingAmount = billingStatus?.remainingAmount ?? (includedAmount - usedAmount);
  const usagePercent = billingStatus?.usagePercent ?? 0;

  return (
    <div className="settings-section">
      <h3>📊 Usage</h3>
      <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "14px" }}>
        {isUnlimited
          ? "Track your usage. You have unlimited access."
          : "Track your usage this billing period."}
      </p>

      {/* Period Selector */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
        {["day", "week", "month"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className="hf-press hf-focus"
            style={{
              padding: "8px 16px",
              background: period === p ? "var(--accent-primary)" : "var(--bg-tertiary)",
              border: period === p ? "none" : "1px solid var(--border-primary)",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "13px",
              fontWeight: "500",
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {p === "day" ? "Today" : `This ${p.charAt(0).toUpperCase() + p.slice(1)}`}
          </button>
        ))}
      </div>

      {/* Usage Progress */}
      {!isUnlimited && !hasNoUsage && (
        <div
          className="hf-panel hf-reveal"
          style={{
            border: "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "24px",
            background: "var(--bg-tertiary)",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <span className="hf-count" style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500" }}>
              ${usedAmount.toFixed(2)} of ${includedAmount.toFixed(2)} used
            </span>
            <span className="hf-count" style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
              ${remainingAmount.toFixed(2)} remaining
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "var(--bg-primary)",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.min(usagePercent, 100)}%`,
                height: "100%",
                background: usagePercent > 90 ? "var(--accent-danger)" : usagePercent > 70 ? "var(--accent-warning)" : "var(--accent-success)",
                transition: "width 0.3s ease",
              }}
            />
          </div>

          {usagePercent >= 80 && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                background: usagePercent >= 95 ? "var(--bg-danger-subtle)" : "var(--bg-warning-subtle)",
                border: `1px solid ${usagePercent >= 95 ? "var(--border-danger)" : "var(--border-warning)"}`,
                borderRadius: "8px",
                fontSize: "13px",
                color: "var(--text-primary)",
              }}
            >
              {usagePercent >= 95
                ? "⚠️ Almost at your limit!"
                : "💡 Approaching your usage limit."}
            </div>
          )}
        </div>
      )}

      {/* Unlimited Access */}
      {isUnlimited && (
        <div
          className="hf-panel hf-reveal"
          style={{
            border: "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "24px",
            background: "var(--bg-secondary)",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <span style={{ fontSize: "40px" }}>⚡</span>
            <div>
              <h4 style={{ margin: 0, color: "var(--text-primary)", fontSize: "18px" }}>
                Unlimited Access
              </h4>
              <p style={{ margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" }}>
                One flat plan, every surface, no caps. Powered by your own
                Claude account — use as much as you want, across Ollie, Mako,
                Orca and Harry.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* No Usage Yet */}
      {hasNoUsage && (
        <div
          className="hf-panel hf-reveal"
          style={{
            border: "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "40px",
            background: "var(--bg-tertiary)",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "48px", display: "block", marginBottom: "16px" }}>📊</span>
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "14px" }}>
            No usage yet this period. Start chatting with Ollie!
          </p>
        </div>
      )}
    </div>
  );
};

export default UsageTab;
